import { useMemo } from "react";

import { Article, ExtendedViewItem } from "@/types/receipt";
import { GLOBAL_WIDGET_TYPES, GlobalWidgetType } from "@/constants/popup";

interface UseExtendedWidgetVisibilityProps {
  article?: Article;
  extended_view_items?: ExtendedViewItem[];
  extended_view_images?: { [key: string]: string };
  currentView:
    | "service_view"
    | "article_view"
    | "top_view"
    | "bottom_view"
    | "return_view"
    | GlobalWidgetType;
}

export const useExtendedWidgetVisibility = ({
  article,
  extended_view_items,
  extended_view_images,
  currentView,
}: UseExtendedWidgetVisibilityProps) => {
  return useMemo(() => {
    const allWidgetsWithImages = (extended_view_items ?? []).map(
      (item, index) => {
        const imageKey = `${item.extended_view_position}_${index}`;
        const imageSrc = extended_view_images?.[imageKey];
        return { ...item, imageSrc, originalIndex: index };
      }
    );

    const visibleWidgets = allWidgetsWithImages.filter((item) => {
      const isZeiptGlobal = item.extended_view_position?.startsWith("zeipt_");

      if (currentView === "service_view") {
        return !isZeiptGlobal && item.widget_button_position === "service_view";
      }

      if (currentView === "top_view") {
        return item.widget_button_position === "top";
      }

      if (currentView === "bottom_view") {
        return item.widget_button_position === "bottom";
      }

      if (currentView === "return_view") {
        return item.widget_button_position === currentView;
      }

      if (isZeiptGlobal) {
        return item.extended_view_position === currentView;
      }

      if (item.widget_button_position === "article") {
        if (currentView !== "article_view") return false;

        if (
          item.article_visibility &&
          item.article_visibility.length > 0 &&
          article?.art_number
        ) {
          return item.article_visibility.includes(article.art_number);
        }

        return true;
      }

      return false;
    });

    const serviceWidgets = allWidgetsWithImages.filter(
      (item) =>
        !item.extended_view_position?.startsWith("zeipt_") &&
        item.widget_button_position === "service_view"
    );

    const articleWidgets = allWidgetsWithImages.filter(
      (item) => item.widget_button_position === "article"
    );

    const categorizedGlobalWidgets = Object.fromEntries(
      GLOBAL_WIDGET_TYPES.map((type) => [
        type,
        allWidgetsWithImages.filter(
          (item) => item.extended_view_position === type
        ),
      ])
    ) as Record<GlobalWidgetType, typeof allWidgetsWithImages>;

    const allGlobalWidgets = allWidgetsWithImages.filter((item) =>
      item.extended_view_position.startsWith("zeipt_")
    );

    const topWidgets = allWidgetsWithImages.filter(
      (item) => item.widget_button_position === "top"
    );

    const bottomWidgets = allWidgetsWithImages.filter(
      (item) =>
        item.widget_button_position === "bottom" &&
        !item.extended_view_position.startsWith("zeipt_")
    );

    const returnWidget = allWidgetsWithImages.filter(
      (item) => item.widget_button_position === "return_view"
    );

    return {
      visibleWidgets,
      serviceWidgets,
      articleWidgets,
      categorizedGlobalWidgets,
      allGlobalWidgets,
      topWidgets,
      bottomWidgets,
      returnWidget,
    };
  }, [article, extended_view_items, extended_view_images, currentView]);
};
